/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  getBraindatePath,
  getBraindateConversation,
} from '@e180/domain/lib/braindate/util';
import { getConversationParticipants } from '@e180/domain/lib/conversation/util';
import { getUserId, getUserFirstName } from '@e180/domain/lib/user/util';

import messages from 'plugin/virtual-braindate/l10n/virtualBraindateL10n';
import ConversationAvatar from 'src/shared/app/conversation/component/avatar/ConversationAvatar';
import Notification from 'src/shared/app/notification/component/Notification';
import { getNotificationData } from 'src/shared/app/notification/util/notificationUtil';
import UserAvatar, {
  small,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import { isConversationAboutGroupBraindate } from 'src/shared/domain/conversation/util/conversationUtil';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { User } from '@e180/domain/lib/user/type';
import type { Notification as NotificationType } from 'src/shared/app/notification/type/notificationTypes';

type Props = {
  notification: NotificationType,
  user: User,
  handleClick: () => void,
  handleDismiss: () => void,
};

const VirtualBraindateReminderNotificationDumb = ({
  notification,
  user,
  handleClick,
  handleDismiss,
}: Props): React.Node => {
  // $FlowIssue
  const braindate: Braindate = getNotificationData(notification);
  const href = getBraindatePath(braindate);
  const conversation = getBraindateConversation(braindate);
  const isGroupBraindate = isConversationAboutGroupBraindate(conversation);
  const participants = getConversationParticipants(conversation);
  const otherParticipants = participants.filter(
    (p) => getUserId(p) !== getUserId(user),
  );

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { braindateSingular } = useEventBraindateName();

  const messageTxt = intl.formatMessage(messages.reminder, {
    isGroupBraindate,
    firstName: otherParticipants.map(getUserFirstName).join(', '),
    braindateSingular,
  });

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const imageElt = isGroupBraindate ? (
    <ConversationAvatar conversation={conversation} />
  ) : (
    <UserAvatar user={otherParticipants[0]} variant={small} />
  );

  return (
    <Notification
      href={href}
      media={imageElt}
      handleClick={handleClick}
      handleDismiss={handleDismiss}
    >
      {messageTxt}
    </Notification>
  );
};

export default compose(React.memo)(VirtualBraindateReminderNotificationDumb);
